"use client";

import "flag-icons/css/flag-icons.min.css";

import { useState } from "react";
import { talentNetworkText } from "@/i18n/talentNetwork.constants";

import messages from "@/i18n/messages/en.json";

import { Button, Skeleton } from "@artha/ui";
import { formatCompactNumber } from "@artha/utils";
import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useTalentNetworkSummary } from "../services/talentNetworkClient";
import type { TalentNetworkSummaryProps } from "../talentNetwork.types";
import {
  getTalentNetworkCountries,
  getTalentNetworkFields,
} from "../talentNetwork.utils";
import {
  TALENT_NETWORK_ACTION_CLASS_NAME,
  TALENT_NETWORK_COLLAPSED_FIELD_LIMIT,
  TALENT_NETWORK_EMPLOYERS_HIRING_MINIMUM,
} from "../talentNetwork.constants";
// Restore with Hired and employer views: import { TALENT_NETWORK_DEMO_ACTIVITY } from "../talentNetwork.constants";
import { PROFILE_HEADER_EMOJI_PATTERN } from "../../candidate-personalization/personalization.constants";
import { PROFILE_CARD_CLASS_NAME } from "../../candidate-personalization/components/profile-cards/profileCards.constants";

export function TalentNetworkSummary({ username, creatorName, onJoin, joinLabel, joinDisabled, showActions = true }: TalentNetworkSummaryProps) {
  const [showAllFields, setShowAllFields] = useState(false);
  const query = useTalentNetworkSummary(username);
  if (query.isLoading) {
    return (
      <div role="status" aria-label={messages.talentNetworkUi.loading_talent_network}>
        <Skeleton className="h-96 w-full rounded-2xl" />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="py-6 text-center">
        <p className="font-semibold text-[#B42318]">{messages.talentNetworkUi.unable_to_load_this_talent_network}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => query.refetch()}
        >{messages.talentNetworkUi.try_again}</Button>
      </div>
    );
  }
  const data = query.data!;
  const fields = getTalentNetworkFields(data);
  const countries = getTalentNetworkCountries(data);
  const showEmployersHiring =
    data.employersHiring != null &&
    data.employersHiring > TALENT_NETWORK_EMPLOYERS_HIRING_MINIMUM;
  const hasMoreFields = fields.length > TALENT_NETWORK_COLLAPSED_FIELD_LIMIT;
  const visibleFields = showAllFields
    ? fields
    : fields.slice(0, TALENT_NETWORK_COLLAPSED_FIELD_LIMIT);
  const displayName = creatorName?.replace(PROFILE_HEADER_EMOJI_PATTERN, "").trim() || username;
  const firstName = displayName.split(" ")[0];
  const defaultJoinLabel = talentNetworkText("join_network", { name: firstName });
  return (
    <section
      aria-label={messages.talentNetworkUi.talent_network}
      className="min-w-0 text-[#101828]"
    >
      <h2 className="text-xl font-bold leading-7 tracking-tight">{messages.talentNetworkUi.join}{displayName}{messages.talentNetworkUi.s_talent_network}</h2>
      <p className="mt-1 text-sm leading-6 text-[#667085]">{messages.talentNetworkUi.your_skills_deserve_to_be_seen_give_them_a}</p>
      <div className="mt-4 space-y-5">
        <section aria-label={messages.talentNetworkUi.network_activity} className={PROFILE_CARD_CLASS_NAME}>
          <dl className={`grid ${showEmployersHiring ? "grid-cols-2 divide-x divide-black/10" : "grid-cols-1"}`}>
            <div className="flex min-w-0 flex-col items-center gap-1 px-3">
              <dt className="text-sm leading-5 text-[#667085]">{messages.talentNetworkUi.members}</dt>
              <dd className="-order-1 text-3xl font-bold tracking-tight tabular-nums">
                {formatCompactNumber(data.professionals)}
              </dd>
            </div>
            {/* Restore Hired when real hiring data is available.
            <div className="flex min-w-0 flex-col items-center gap-1 px-3">
              <dt className="text-sm leading-5 text-[#667085]">
                Hired
              </dt>
              <dd className="-order-1 text-3xl font-bold tracking-tight tabular-nums">
                {TALENT_NETWORK_DEMO_ACTIVITY.hired}
              </dd>
            </div>
            */}
            {showEmployersHiring && (
              <div className="flex min-w-0 flex-col items-center gap-1 px-3 text-center">
                <dt className="text-sm leading-5 text-[#667085]">{messages.talentNetworkUi.employers_hiring}</dt>
                <dd className="-order-1 text-3xl font-bold tracking-tight tabular-nums">
                  {formatCompactNumber(data.employersHiring!)}
                </dd>
              </div>
            )}
          </dl>

          {/* Restore employer views when real profile-view data is available.
          <p className="text-sm leading-6 text-[#475467]">
            Employers viewed {TALENT_NETWORK_DEMO_ACTIVITY.viewedMembers.toLocaleString("en-US")} members&apos; profiles {TALENT_NETWORK_DEMO_ACTIVITY.profileViews.toLocaleString("en-US")} times.
          </p>
          */}

          {data.professionals === 0 && (
            <p className="mt-2 text-sm leading-6 text-[#667085]">{messages.talentNetworkUi.be_the_first_to_join_this_network}</p>
          )}
        </section>
        {(fields.length > 0 || countries.length > 0) && (
          <section aria-label={messages.talentNetworkUi.network_breakdown} className={PROFILE_CARD_CLASS_NAME}>
            <h3 className="text-lg font-bold">{messages.talentNetworkUi.who_s_in_the_network}</h3>
            {fields.length > 0 && (
              <div>
                <figure aria-label={messages.talentNetworkUi.network_field_distribution} className="mt-4">
                  <div aria-hidden="true" className="flex h-3 w-full overflow-hidden rounded-full">
                    {fields.map((item) => (
                      <span
                        key={item.value}
                        className="h-full shrink-0"
                        style={{ backgroundColor: item.color, width: `${item.percentage}%` }}
                      />
                    ))}
                  </div>
                </figure>
                <ul aria-label={messages.talentNetworkUi.top_fields} className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5">
                  {visibleFields.map((item) => (
                    <li
                      key={item.value}
                      className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-xs leading-5"
                    >
                      <span aria-hidden="true" className="size-2 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="min-w-0 break-words font-semibold">{item.label}</span>{" "}
                      <span className="shrink-0 tabular-nums text-[#667085]">{item.percentage}{"%"}</span>
                    </li>
                  ))}
                </ul>
                {hasMoreFields && (
                  <button
                    type="button"
                    aria-expanded={showAllFields}
                    onClick={() => setShowAllFields((current) => !current)}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0D41E1] hover:underline"
                  >
                    {showAllFields
                      ? messages.talentNetworkUi.show_less
                      : messages.talentNetworkUi.show_more}
                    {showAllFields ? (
                      <ChevronUp aria-hidden="true" className="size-4" />
                    ) : (
                      <ChevronDown aria-hidden="true" className="size-4" />
                    )}
                  </button>
                )}
              </div>
            )}
            {countries.length > 0 && (
              <div className={fields.length > 0 ? "mt-5 border-t border-black/10 pt-4" : "mt-4"}>
                <ul
                  aria-label={messages.talentNetworkUi.locations}
                  className="flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-xs leading-5 text-[#667085]"
                >
                  {countries.map((country) => (
                    <li key={country.code} aria-label={`${country.label} ${country.percentage}%`} className="flex items-center gap-1.5">
                      <span aria-hidden="true" className={`fi fi-${country.code} shrink-0 text-base leading-none`} />
                      <span className="tabular-nums">{country.percentage}{"%"}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      </div>
      {showActions ? <div className="mt-5 space-y-3">
        <Button
          asChild={!onJoin}
          onClick={onJoin}
          disabled={joinDisabled}
          className={TALENT_NETWORK_ACTION_CLASS_NAME}
        >
          {onJoin ? <>{joinLabel || defaultJoinLabel}<ArrowUpRight aria-hidden="true" className="size-4" /></> : (
            <a href={`/@${username}/talent-network`}>
              {joinLabel || defaultJoinLabel}
              <ArrowUpRight aria-hidden="true" className="size-4" />
            </a>
          )}
        </Button>
        {/* Restore the Hire from Network action when its destination is enabled.
        <Button
          type="button"
          variant="outline"
          disabled
          className={`${TALENT_NETWORK_ACTION_CLASS_NAME} rounded-lg text-[#0D41E1] shadow-[0_4px_0_0_black] disabled:bg-white`}
        >{messages.talentNetworkUi.hire_from}{firstName}{messages.talentNetworkUi.s_network}</Button>
        */}
      </div> : null}
    </section>
  );
}
